import { useEffect, useRef, useState } from 'react';
import styles from './Reveal.module.css';

/** Fades children up once when they enter the viewport. */
/** `onReveal` fires once, in the same tick the element becomes visible. */
export function Reveal({ as: Tag = 'div', delay = 0, className = '', onReveal, children, ...rest }) {
  const ref = useRef(null);
  // No observer support: show immediately rather than never. Server renders start hidden, like the client.
  const [visible, setVisible] = useState(
    () => typeof window !== 'undefined' && !('IntersectionObserver' in window)
  );

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible(true);
        onReveal?.();
        observer.disconnect();
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observe once on mount
  }, []);

  return (
    <Tag
      ref={ref}
      className={`${styles.reveal} ${className}`}
      data-visible={visible}
      style={{ '--reveal-delay': `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
